/**
 * RatingsManager Component
 * Admin interface for viewing and managing user ratings
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Star, Users, TrendingUp, BarChart3, Search, 
  RefreshCw, ChevronDown, ChevronUp, User, Calendar,
  MessageSquare, ThumbsUp, ThumbsDown, Filter
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { API } from '../App';

const RatingsManager = () => {
  const [stats, setStats] = useState(null);
  const [allRatings, setAllRatings] = useState([]);
  const [userRatings, setUserRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedRating, setExpandedRating] = useState(null);
  const [filterRating, setFilterRating] = useState('all');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/ratings/stats`),
        axios.get(`${API}/super-admin/users`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      
      // Fetch ratings for users who have been rated
      const ratingsPromises = usersRes.data.slice(0, 50).map(async (user) => {
        try {
          const res = await axios.get(`${API}/ratings/user/${encodeURIComponent(user.email)}`);
          return { email: user.email, data: res.data };
        } catch {
          return { email: user.email, data: { total_ratings: 0, average_rating: 0, ratings: [] } };
        }
      });
      
      const ratingsResults = await Promise.all(ratingsPromises);
      const ratingsMap = {};
      ratingsResults.forEach(r => {
        if (r.data.total_ratings > 0) {
          ratingsMap[r.email] = r.data;
        }
      });
      setUserRatings(ratingsMap);
      
      // Collect all ratings
      const all = [];
      Object.entries(ratingsMap).forEach(([email, data]) => {
        data.ratings.forEach(rating => {
          all.push({ ...rating, rated_email: email });
        });
      });
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAllRatings(all);
      
    } catch (error) {
      toast.error('Erreur lors du chargement des évaluations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating, size = 'sm') => {
    const sizeClass = size === 'lg' ? 'h-6 w-6' : size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClass} ${
              star <= rating 
                ? 'fill-amber-400 text-amber-400' 
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        ))}
      </div>
    );
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600 bg-green-50';
    if (rating >= 3.5) return 'text-blue-600 bg-blue-50';
    if (rating >= 2.5) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-CA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter ratings based on search and rating filter
  const filteredRatings = allRatings.filter(rating => {
    const matchesSearch = !searchQuery || 
      rating.rated_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rating.rater_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rating.comment?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterRating === 'all' || 
      (filterRating === 'positive' && rating.rating >= 4) ||
      (filterRating === 'neutral' && rating.rating === 3) ||
      (filterRating === 'negative' && rating.rating <= 2);
    
    return matchesSearch && matchesFilter;
  });

  // Get users with ratings
  const usersWithRatings = Object.entries(userRatings)
    .map(([email, data]) => {
      const user = users.find(u => u.email === email) || { email, name: email };
      return { ...user, ...data };
    })
    .sort((a, b) => b.total_ratings - a.total_ratings);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="text-lg font-semibold">Gestion des évaluations</h3>
          <p className="text-sm text-gray-500">
            Consultez les avis et notes de vos utilisateurs
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-ratings-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" data-testid="ratings-overview-tab">
            <BarChart3 className="h-4 w-4 mr-2" />
            Aperçu
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="ratings-users-tab">
            <Users className="h-4 w-4 mr-2" />
            Par utilisateur
          </TabsTrigger>
          <TabsTrigger value="recent" data-testid="ratings-recent-tab">
            <MessageSquare className="h-4 w-4 mr-2" />
            Récentes
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {stats && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Star className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.average_rating || 0}</p>
                      <p className="text-xs text-gray-500">Note moyenne</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.total_ratings || 0}</p>
                      <p className="text-xs text-gray-500">Total évaluations</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <ThumbsUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {stats.rating_distribution ? 
                          (stats.rating_distribution[4] || 0) + (stats.rating_distribution[5] || 0) : 0}
                      </p>
                      <p className="text-xs text-gray-500">Positives (4-5★)</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <ThumbsDown className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {stats.rating_distribution ? 
                          (stats.rating_distribution[1] || 0) + (stats.rating_distribution[2] || 0) : 0}
                      </p>
                      <p className="text-xs text-gray-500">Négatives (1-2★)</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Rating Distribution */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Distribution des notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = stats.rating_distribution?.[star] || 0;
                      const percentage = stats.total_ratings > 0 
                        ? Math.round((count / stats.total_ratings) * 100) 
                        : 0;
                      
                      return (
                        <div key={star} className="flex items-center gap-3">
                          <div className="flex items-center gap-1 w-16">
                            <span className="text-sm font-medium">{star}</span>
                            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          </div>
                          <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                star >= 4 ? 'bg-green-500' : star === 3 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="w-16 text-right">
                            <span className="text-sm text-gray-600">{count}</span>
                            <span className="text-xs text-gray-400 ml-1">({percentage}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Satisfaction Survey Summary */}
              {stats.satisfaction_summary && Object.keys(stats.satisfaction_summary).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Résumé satisfaction</CardTitle>
                    <CardDescription>Moyennes des questions de satisfaction</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(stats.satisfaction_summary).map(([key, value]) => (
                        <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 capitalize mb-1">
                            {key.replace(/_/g, ' ')}
                          </p>
                          <p className={`text-xl font-bold ${getRatingColor(value)}`}>
                            {value}/5
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {!stats || stats.total_ratings === 0 ? (
            <Card className="p-8 text-center">
              <Star className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune évaluation pour le moment</p>
              <p className="text-sm text-gray-400 mt-1">
                Les évaluations apparaîtront ici après les transactions complétées
              </p>
            </Card>
          ) : null}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4 mt-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              className="pl-10"
              data-testid="search-user-ratings"
            />
          </div>

          {/* Users with ratings */}
          <div className="space-y-3">
            {usersWithRatings.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Aucun utilisateur avec des évaluations</p>
              </Card>
            ) : (
              usersWithRatings
                .filter(user => 
                  !searchQuery || 
                  user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  user.name?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((user) => (
                  <Card 
                    key={user.email} 
                    className={`cursor-pointer transition-all ${
                      selectedUser === user.email ? 'ring-2 ring-brand-500' : ''
                    }`}
                    onClick={() => setSelectedUser(selectedUser === user.email ? null : user.email)}
                    data-testid={`user-rating-card-${user.email}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-blue-500 flex items-center justify-center text-white font-medium">
                            {(user.name || user.email).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{user.name || user.email}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              {renderStars(Math.round(user.average_rating))}
                              <span className="font-bold ml-1">{user.average_rating}</span>
                            </div>
                            <p className="text-xs text-gray-500">{user.total_ratings} évaluation(s)</p>
                          </div>
                          {selectedUser === user.email ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Expanded user ratings */}
                      {selectedUser === user.email && user.ratings && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          {user.ratings.map((rating, idx) => (
                            <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                {renderStars(rating.rating)}
                                <span className="text-xs text-gray-500">
                                  {formatDate(rating.created_at)}
                                </span>
                              </div>
                              {rating.comment && (
                                <p className="text-sm text-gray-700 italic">"{rating.comment}"</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {rating.rater_role === 'buyer' ? 'Acheteur' : 'Vendeur'}
                                </Badge>
                                <span className="text-xs text-gray-400">
                                  Par: {rating.rater_email}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
            )}
          </div>
        </TabsContent>

        {/* Recent Ratings Tab */}
        <TabsContent value="recent" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant={filterRating === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilterRating('all')}
              >
                Toutes
              </Button>
              <Button 
                variant={filterRating === 'positive' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilterRating('positive')}
                className={filterRating === 'positive' ? 'bg-green-600' : ''}
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                4-5★
              </Button>
              <Button 
                variant={filterRating === 'negative' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilterRating('negative')}
                className={filterRating === 'negative' ? 'bg-red-600' : ''}
              >
                <ThumbsDown className="h-4 w-4 mr-1" />
                1-2★
              </Button>
            </div>
          </div>

          {/* Recent ratings list */}
          <div className="space-y-3">
            {filteredRatings.length === 0 ? (
              <Card className="p-8 text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Aucune évaluation trouvée</p>
              </Card>
            ) : (
              filteredRatings.slice(0, 50).map((rating, idx) => (
                <Card 
                  key={idx}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setExpandedRating(expandedRating === idx ? null : idx)}
                  data-testid={`recent-rating-${idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {renderStars(rating.rating, 'md')}
                          <Badge className={`${getRatingColor(rating.rating)} ml-2`}>
                            {rating.rating}/5
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <User className="h-4 w-4" />
                          <span className="font-medium">{rating.rated_email}</span>
                          <span className="text-gray-400">•</span>
                          <Badge variant="outline" className="text-xs">
                            {rating.rater_role === 'buyer' ? 'Acheteur' : 'Vendeur'}
                          </Badge>
                        </div>
                        {rating.comment && (
                          <p className={`text-sm text-gray-700 mt-2 ${
                            expandedRating !== idx ? 'line-clamp-2' : ''
                          }`}>
                            "{rating.comment}"
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          {formatDate(rating.created_at)}
                        </div>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {expandedRating === idx && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Évalué par</p>
                            <p className="font-medium">{rating.rater_email}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Transaction</p>
                            <p className="font-medium font-mono text-xs">
                              {rating.transaction_id?.slice(0, 8)}...
                            </p>
                          </div>
                        </div>
                        {rating.satisfaction_survey && Object.keys(rating.satisfaction_survey).length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm text-gray-500 mb-2">Satisfaction détaillée</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(rating.satisfaction_survey).map(([key, value]) => (
                                <Badge key={key} variant="outline" className="text-xs">
                                  {key.replace(/_/g, ' ')}: {value}/5
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {filteredRatings.length > 50 && (
            <p className="text-center text-sm text-gray-500">
              Affichage des 50 premières évaluations sur {filteredRatings.length}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RatingsManager;
